import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { pnaPriceQueryOptions } from "~/domains/pna/pnaQueries";
import { toOutcomeFromQuery } from "~/domains/pna/pnaSnapshot";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { selectCanQueryPrice, selectKbHash } from "~/features/configurator/Linear/redux/selectors";
import { useLinearProductInfo } from "~/features/configurator/Linear/redux/useLinearProductInfo";

interface Props {
  priceReference: string;
}

export const userCanPurchaseLinear = () => userHasPermission(Permission.LINEAR_PURCHASE);

export const useLinearPrice = ({ priceReference }: Props) => {
  const configuratorProduct = useLinearProductInfo();
  const canQuery = useSelector(selectCanQueryPrice);

  // The hash is a refresh signal for the one current configuration, not query identity.
  const kbHash = useSelector(selectKbHash);
  const productCode = configuratorProduct.hybrisCode;

  const priceOptions = useMemo(
    () =>
      pnaPriceQueryOptions({
        productCode,
        configId: configuratorProduct.configId,
        priceReference,
        enabled: canQuery,
      }),
    [canQuery, configuratorProduct.configId, priceReference, productCode],
  );
  const priceQuery = useQuery(priceOptions);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (canQuery) {
      void queryClient.invalidateQueries({ queryKey: priceOptions.queryKey, exact: true });
    }
  }, [canQuery, kbHash, priceOptions.queryKey, queryClient]);
  const price = toOutcomeFromQuery(priceQuery, priceOptions.enabled === true);

  return {
    priceInfo: price.status === "success" ? price.data[0] ?? null : null,
    errorLoadingPrice: price.status === "error",
    isLoadingPrice: price.status === "pending",
  };
};
